import { useEffect, useState } from "react";
import DashboardShell from "@/components/dashboard/DashboardShell";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Trophy, Calendar, Newspaper, MapPin, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

export default function OfficialsDashboard() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    api.get(`/championships`).then((r) => setEvents(r.data.slice(0, 8))).catch(() => {});
    api.get(`/news`).then((r) => setNews(r.data.slice(0, 4))).catch(() => {});
  }, []);

  const upcoming = events.filter((e) => (e.start_date || "") >= new Date().toISOString().slice(0, 10));
  const past = events.filter((e) => (e.start_date || "") < new Date().toISOString().slice(0, 10));

  return (
    <DashboardShell title="Officials Portal" eyebrow={`Welcome, ${user?.name?.split(" ")[0] || "Official"}`}>
      <div className="bg-gradient-to-br from-nkf-blue to-nkf-navy text-white rounded-md p-6 relative overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes" />
        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-nkf-gold">Federation Official</div>
            <div className="font-display text-2xl sm:text-3xl mt-2">{user?.name}</div>
            <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <Row label="Official ID" value={user?.membership_no || "Pending"} />
              <Row label="State" value={user?.state || "—"} />
              <Row label="District" value={user?.district || "—"} />
              <Row label="Status" value="Active" />
            </div>
          </div>
          <div className="md:justify-self-end flex items-center gap-4">
            <ShieldCheck className="h-10 w-10 text-nkf-gold" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-nkf-gold">Federation Access</div>
              <div className="font-display text-lg mt-1">Referee · Judge · Technical</div>
            </div>
          </div>
        </div>
      </div>

      <section className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl text-nkf-blue flex items-center gap-2"><Trophy className="h-5 w-5 text-nkf-red" /> Upcoming Assignments</h2>
          <Link to="/championships" className="text-sm font-semibold text-nkf-blue hover:text-nkf-red">All events →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {upcoming.map((c) => (
            <Link key={c.id} to={`/championships/${c.id}`} className="bg-white border border-slate-200 rounded-md p-4 hover:border-nkf-blue/40 transition">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-widest text-nkf-red font-semibold">{c.level}</div>
                <div className="text-[10.5px] uppercase tracking-widest bg-nkf-gold/15 text-nkf-blue px-2 py-0.5 rounded">{c.discipline}</div>
              </div>
              <div className="font-display text-base text-nkf-blue mt-1.5">{c.title}</div>
              <div className="text-xs text-slate-500 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {c.start_date} – {c.end_date}</span>
                <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {c.location}</span>
              </div>
            </Link>
          ))}
          {!upcoming.length && <div className="md:col-span-2 p-6 border border-dashed rounded-md text-slate-500 text-sm text-center">No upcoming assignments.</div>}
        </div>
      </section>

      {past.length > 0 && (
        <section className="mt-8">
          <h2 className="font-display text-xl text-nkf-blue mb-4">Past Events</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {past.slice(0, 6).map((c) => (
              <div key={c.id} className="bg-white border border-slate-200 rounded-md p-4 opacity-90">
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">{c.level}</div>
                <div className="font-display text-base text-nkf-blue mt-1">{c.title}</div>
                <div className="text-xs text-slate-500 mt-1">{c.start_date}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {news.length > 0 && (
        <section className="mt-8">
          <h2 className="font-display text-xl text-nkf-blue mb-4 flex items-center gap-2"><Newspaper className="h-5 w-5 text-nkf-red" /> Federation Updates</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {news.map((n) => (
              <article key={n.id} className="bg-white border border-slate-200 rounded-md p-4">
                <div className="text-[11px] uppercase tracking-widest text-nkf-red font-semibold">{n.category}</div>
                <div className="font-display text-base text-nkf-blue mt-1">{n.title}</div>
                <p className="text-xs text-slate-600 mt-1 line-clamp-2">{n.body}</p>
              </article>
            ))}
          </div>
        </section>
      )}
    </DashboardShell>
  );
}

function Row({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-white/60">{label}</div>
      <div className="font-display tracking-wide truncate">{value}</div>
    </div>
  );
}
